import { useState } from 'react';
import { doc, updateDoc, deleteDoc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '../../firebase';

/**
 * Hook personnalisé pour gérer la suppression d'utilisateurs de copropriétés
 * Gère les quatre types d'utilisateurs : providers, owners, tenants, rentalManager
 */
const useDeleteUserFromCoownership = () => {
  const db = getFirestore(app);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Supprime un utilisateur d'une copropriété selon son type
   * @param {string} userId - ID de l'utilisateur à supprimer
   * @param {string} coownershipId - ID de la copropriété
   * @param {string} userType - Type d'utilisateur ('provider', 'owner', 'tenant', 'rentalManager')
   */
  const deleteUserFromCoownership = async (
    userId, 
    coownershipPath, 
    userType, 
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      // Validation des paramètres
      if (!userId || !coownershipPath || !userType) {
        throw new Error('Paramètres manquants : userId, coownershipId et userType sont requis');
      }

      // Validation du type d'utilisateur
      const validUserTypes = ['provider', 'owner', 'tenant', 'rentalManager'];
      if (!validUserTypes.includes(userType)) {
        throw new Error(`Type d'utilisateur invalide. Types acceptés : ${validUserTypes.join(', ')}`);
      }

      // Définir les chemins selon le type d'utilisateur
      const collectionPaths = {
        provider: `${coownershipPath}/usersProvider/${userId}`,
        owner: `${coownershipPath}/owner/${userId}`,
        tenant: `${coownershipPath}/tenant/${userId}`,
        rentalManager: `${coownershipPath}/usersRentalManager/${userId}`
      };

      const userRef = doc(db, `users/${userId}`);
      const docToDeleteRef = doc(db, collectionPaths[userType]);

      // 1. Récupérer les données actuelles de l'utilisateur
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error(`Utilisateur avec l'ID ${userId} introuvable`);
      }

      const userData = userDoc.data();
      
      // 2. Filtrer les copropriétés pour supprimer celle à supprimer
      if (userData.coownershipId && Array.isArray(userData.coownershipId)) {
        const updatedCoownerships = userData.coownershipId.filter(
          (coownership) => coownership.coownershipReference.path !== coownershipPath
        );

        // 3. Mettre à jour le document utilisateur seulement si des modifications sont nécessaires
        if (updatedCoownerships.length !== userData.coownershipId.length) {
          let updateData = { coownershipId: updatedCoownerships };

          // 4. Vérifier si on doit mettre à jour lastCoownershipLoaded
          if (userData.lastCoownershipLoaded && 
              userData.lastCoownershipLoaded.path === coownershipPath) {
            
            updateData.lastCoownershipLoaded = updatedCoownerships.length > 0 
              ? updatedCoownerships[0].coownershipReference
              : null;
          }

          // 5. Mettre à jour le document utilisateur dans Firestore
          await updateDoc(userRef, updateData);
        }
      }

      // 6. Supprimer le document de la collection coownership
      await deleteDoc(docToDeleteRef);

      setIsLoading(false);
      return { success: true, message: 'Utilisateur supprimé avec succès' };

    } catch (err) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', err);
      setError(err.message);
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  };

  /**
   * Fonction utilitaire pour supprimer un prestataire
   */
  const deleteProviderFromCoownership = (userId, coownershipPath) => {
    return deleteUserFromCoownership(userId, coownershipPath, 'provider');
  };

  /**
   * Fonction utilitaire pour supprimer un propriétaire
   */
  const deleteOwnerFromCoownership = (userId, coownershipPath) => {
    return deleteUserFromCoownership(userId, coownershipPath, 'owner');
  };

  /**
   * Fonction utilitaire pour supprimer un locataire
   */
  const deleteTenantFromCoownership = (userId, coownershipPath) => {
    return deleteUserFromCoownership(userId, coownershipPath, 'tenant');
  };

  /**
   * Fonction utilitaire pour supprimer un gestionnaire locatif
   */
  const deleteRentalManagerFromCoownership = (userId, coownershipPath) => {
    return deleteUserFromCoownership(userId, coownershipPath, 'rentalManager');
  };

  /**
   * Fonction utilitaire pour supprimer une copropriété d'un compte syndic
   */
  const deleteCoownershipFromSyndic = async (userId, coownershipPath) => {
    setIsLoading(true);
    setError(null);

    try {
      // Validation des paramètres
      if (!userId || !coownershipPath) {
        throw new Error('Paramètres manquants : userId, coownershipId et userType sont requis');
      }

      const userRef = doc(db, `users/${userId}`);

      // 1. Récupérer les données actuelles de l'utilisateur
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error(`Utilisateur avec l'ID ${userId} introuvable`);
      }

      const userData = userDoc.data();
      
      // 2. Filtrer les copropriétés pour supprimer celle à supprimer
      if (userData.coownershipId && Array.isArray(userData.coownershipId)) {
        const updatedCoownerships = userData.coownershipId.filter(
          (coownership) => coownership.coownershipReference.path !== coownershipPath
        );

        // 3. Mettre à jour le document utilisateur seulement si des modifications sont nécessaires
        if (updatedCoownerships.length !== userData.coownershipId.length) {
          let updateData = { coownershipId: updatedCoownerships };

          // 4. Vérifier si on doit mettre à jour lastCoownershipLoaded
          if (userData.lastCoownershipLoaded && 
              userData.lastCoownershipLoaded.path === coownershipPath) {
            
            updateData.lastCoownershipLoaded = updatedCoownerships.length > 0 
              ? updatedCoownerships[0].coownershipReference
              : null;
          }

          // 5. Mettre à jour le document utilisateur dans Firestore
          await updateDoc(userRef, updateData);
        }
      }

      setIsLoading(false);
      return { success: true, message: 'Utilisateur supprimé avec succès' };

    } catch (err) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', err);
      setError(err.message);
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  };

  return {
    deleteUserFromCoownership,
    deleteProviderFromCoownership,
    deleteOwnerFromCoownership,
    deleteTenantFromCoownership,
    deleteCoownershipFromSyndic,
    deleteRentalManagerFromCoownership,
    isLoading,
    error
  };
};

export default useDeleteUserFromCoownership;